import { Attr, HasMany, HasOne, Model } from "spraypaint";

import { contractTypes } from "shared/constants";
import { ApplicationRecord } from "shared/services/spraypaint";

import type ActiveStorageAttachment from "./ActiveStorageAttachment";
import type ContractsFeature from "./ContractsFeature";
import type Organisation from "./Organisation";
import type OrganisationContractService from "./OrganisationContractService";
import type User from "./User";

enum ContractType {
  sla = 1,
  additional_daily_email_report = 2,
  additional_transcripts = 3,
  additional_media_item_downloads = 4,
  additional_print_allowance = 5,
  additional_users = 6,
  additional_media_analysis_reporting = 7,
  additional_scope = 8,
  additional_social_monitoring_influencer = 9,
  additional_social_monitoring_engage_only = 10,
  additional_social_monitoring = 11,
  additional_keywords_or_clients = 12,
  additional_service = 13,
}

@Model()
class OrganisationContract extends ApplicationRecord {
  static jsonapiType = "organisation_contracts";

  @Attr() allowRolling: boolean;

  @HasMany() attachments: ActiveStorageAttachment[];

  @Attr() billingCycle: number;

  @Attr() billMonthInAdvance: boolean;

  @Attr() contractExpiry: string;

  @Attr() contractStart: string;

  @Attr() contractType: ContractType;

  @Attr() copyrightTier: string;

  @Attr({ persist: false }) createdAt: string;

  @Attr() customerSla: string;

  @Attr() effectiveExpiryDate: string;

  @Attr() excludeFromArr: boolean;

  @Attr() expired: boolean;

  @Attr() externalSource: string;

  @HasMany() features: ContractsFeature[];

  @Attr() invoiceDescription: string;

  @Attr() invoiceSeparately: boolean;

  @Attr() matchSlaBillingCycle: boolean;

  @Attr() maxClients: number;

  @Attr() maxUsers: number;

  @Attr() notes: string;

  @Attr({ persist: false }) onlineContentRevenueShare: number;

  @HasOne() organisation: Organisation;

  @HasMany()
  organisationContractServices: OrganisationContractService[];

  @Attr() organisationId: number;

  @Attr() parentId: number;

  @Attr() priceIncludingTax: number;

  @Attr() pricePerMonth: number;

  @Attr() reviewed: boolean;

  @Attr() rollingContract: boolean;

  @Attr() rollingContractExpiry: string;

  @HasOne() seller: User;

  @Attr() sellerId: number;

  @Attr() taxAmount: number;

  @Attr() tier: number;

  @Attr() tierDescription: string;

  static active() {
    return this.where({ active: { eq: true } });
  }

  static activeSLA() {
    return this.order({ contractStart: "desc" }).where({
      active: { eq: true },
      contractType: ContractType.sla,
    });
  }

  static notExpired() {
    return this.where({ expired: { eq: false } });
  }

  static notExpiredSLA() {
    return this.order({ contractStart: "desc" }).where({
      contractType: 1,
      expired: { eq: false },
    });
  }

  static SLADescending() {
    return this.order({ contractStart: "desc" }).where({
      contractType: ContractType.sla,
    });
  }

  isSLA() {
    return this.contractType === contractTypes.SLA;
  }
}

export default OrganisationContract;
